import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../store";
import { useAppDispatch } from "../store/hooks";
import { notificationAdd } from "../actions/notification";
import useCurrentResourceCentre from "./useCurrentResourceCentre";
import { getCurrentClientQueueNumber } from "../api/clientQueueNumber";
import { ServiceProvider } from "../interfaces/ServiceProvidersInterface";

interface PropType {
  serviceProviderId?: number;
}

// eslint-disable-next-line import/prefer-default-export
export const useClientQueueNumber = ({
  serviceProviderId
}: PropType): {
  queueNumber: number;
  loading: boolean;
  refetch: () => void;
} => {
  const dispatch = useAppDispatch();
  const rc = useCurrentResourceCentre();
  const resourceCentreId = rc?.id;
  const isQueueEnabled = rc?.settings?.queueSettings?.enableQueueSystem;

  const serviceProvider = useSelector((state: RootState) =>
    state.resources.resourceCentreServiceProviders.find(
      (sp: ServiceProvider) => sp.id === serviceProviderId
    )
  );

  const [loading, setLoading] = useState(false);
  const [queueNumber, setQueueNumber] = useState<number>(0);

  const fetchQueue = async () => {
    if (!resourceCentreId || !serviceProviderId) return;

    try {
      setLoading(true);

      const res = await getCurrentClientQueueNumber({
        resourceCentreId,
        serviceProviderId,
        departmentId: serviceProvider.departmentId || null
      });

      setQueueNumber(res.data.queueNumber);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          message: err?.message || err?.data?.message || "Error while fetching queue number",
          variant: "error",
          autoTimeout: true
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isQueueEnabled) {
      fetchQueue();
    }
  }, [
    dispatch,
    isQueueEnabled,
    resourceCentreId,
    serviceProviderId,
    serviceProvider?.id,
    serviceProvider?.departmentId
  ]);

  return { queueNumber: queueNumber + 1, loading, refetch: fetchQueue };
};
